import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Title } from '@angular/platform-browser'

import { catchError, from, switchMap, tap, EMPTY, lastValueFrom } from 'rxjs'

import { ResetPasswordComponent } from './reset-password/reset-password.component'
import {
  AuthService,
  LoginResult,
  MFA_SETTINGS_PATH,
} from '../../services/auth.service'
import { UserService } from '../../services/user/user.service'
import { LoginFlowService } from '../../services/login-flow.service'
import { Auth } from '@angular/fire/auth'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { getTitle } from '../../util/accessibility'
import { captureException } from '../../util/captureException'
import { isExpectedAuthError } from '../../util/isExpectedAuthError'

interface AuthParam {
  mode:
    | 'resetPassword'
    | 'recoverEmail'
    | 'verifyEmail'
    | 'revertSecondFactorAddition'
  oobCode: string
  apiKey: string
  continueUrl: string
  lang: string
}

@UntilDestroy()
@Component({
  standalone: true,
  imports: [ResetPasswordComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (authParam?.mode === 'resetPassword') {
      <app-reset-password
        [isLoading]="isLoading"
        (passwordConfirmed)="this.updatePassword($event)"
      ></app-reset-password>
    }
  `,
})
export class AuthActionComponent implements OnInit {
  email: string | null = null
  authParam: AuthParam | null = null
  isLoading = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private loginFlow: LoginFlowService,
    private auth: Auth,
    private titleService: Title,
    private userService: UserService,
  ) {
    this.titleService.setTitle(getTitle('パスワードを設定'))
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        tap((params) => {
          this.authParam = {
            mode: params.mode,
            oobCode: params.oobCode,
            apiKey: params.apiKey,
            continueUrl: params.continueUrl,
            lang: params.lang,
          }
        }),
        switchMap((params) => {
          if (!params.mode) {
            // mode 無しの /auth/action アクセスは Firebase のメールリンク外からの到達（bot・壊れたリンク）で、
            // Sentry へ送らず再送画面へ誘導するだけにする
            return from(this.goToResendPage())
          }
          if (this.authParam?.mode === 'resetPassword') {
            return from(
              this.authService.verifyPasswordResetCode(params.oobCode),
            ).pipe(
              tap((email) => {
                this.email = email
              }),
            )
          } else if (this.authParam?.mode === 'revertSecondFactorAddition') {
            return from(
              this.router.navigate(['/login'], {
                queryParams: {
                  error: 'cannot_revert_second_factor_error',
                },
              }),
            )
          } else if (this.authParam?.mode === 'verifyEmail') {
            return from(this.verifyEmail(params.oobCode))
          }
          throw new Error('Invalid mode: ' + this.authParam?.mode)
        }),
        catchError((error) => {
          if (!isExpectedAuthError(error)) {
            captureException(error)
          }
          return from(this.goToResendPage()).pipe(switchMap(() => EMPTY))
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  async updatePassword(password: string): Promise<void> {
    if (!this.authParam || !this.email) {
      this.goToResendPage()
      return
    }
    this.isLoading = true
    try {
      // update password
      await this.authService.confirmPasswordReset(
        this.authParam.oobCode,
        password,
      )
    } catch {
      await this.goToResendPage()
      return
    }

    this.authService
      .loginWithEmailAndPassword(this.email!, password)
      .then(
        async (result: LoginResult) => {
          await this.loginFlow.continueAfterFirstFactor(result)
        },
        (error) => {
          let errorMessage = 'ログインに失敗しました。'
          let unhandledError = false
          if (error.code === 'auth/too-many-requests') {
            errorMessage =
              'このアカウントへのアクセスは、多くのログイン試行が失敗したため、一時的に無効化されています。すぐにアカウントを復元するには、パスワードをリセットするか、後で再試行してください。'
          } else {
            errorMessage = `${error.code}: ${error.message}`
            unhandledError = true
          }
          alert(errorMessage)

          if (unhandledError) {
            throw error
          }
        },
      )
      .finally(() => {
        this.isLoading = false
      })
  }

  async verifyEmail(oobCode: string): Promise<void> {
    try {
      await this.authService.applyActionCode(oobCode)
    } catch {
      alert(
        'リンクが無効か期限切れです。設定画面からもう一度確認メールを送ってください',
      )
      await this.router.navigate(['/login'])
      return
    }
    await this.authService.waitForAuthStateReady()
    if (this.auth.currentUser) {
      try {
        await this.authService.reloadCurrentUser()
      } catch (error) {
        captureException(error)
      }
      alert('メールアドレスを確認しました')
      let status
      try {
        status = await lastValueFrom(this.userService.getMfaStatus())
      } catch {
        await this.router.navigate(['/login'])
        return
      }
      if (status.requiredForUser && status.methods.length === 0) {
        this.authService.setReturnUrl(MFA_SETTINGS_PATH)
        await this.loginFlow.navigateToEnrollment(status)
      } else {
        await this.router.navigate([MFA_SETTINGS_PATH])
      }
    } else {
      await this.router.navigate(['/login'], {
        queryParams: { info: 'email_verified' },
      })
    }
  }

  private async goToResendPage(): Promise<void> {
    await this.router.navigate(['/forgot-password'], {
      state: {
        message:
          'リンクの有効期限が切れました。メールアドレスを入力して、リンクを再送してください。',
      },
    })
  }
}
